import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
// 文言生成ロジックは lib へ移動
import { createPokesolText } from '../../lib/formatting';
import { PokemonDefenceInterface } from '../../pokemon/pokemon';
import { StatInputs } from '../../types/stat';

interface PokesolTextModalProps {
  open: boolean;
  onClose: () => void;
  pokemon: PokemonDefenceInterface;
  statInputs: StatInputs;
}

const PokesolTextModal: React.FC<PokesolTextModalProps> = ({
  open,
  onClose,
  pokemon,
  statInputs
}) => {
  const [toasts, setToasts] = useState<Array<{
    id: string;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>>([]);
  const pokesolText = createPokesolText(pokemon, statInputs);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pokesolText);
      const newToast = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        message: 'クリップボードにコピーしました！',
        severity: 'success' as const
      };
      setToasts(prev => [...prev, newToast]);
    } catch (err) {
      console.error('クリップボードへのコピーに失敗しました:', err);
      const errorToast = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        message: 'コピーに失敗しました',
        severity: 'error' as const
      };
      setToasts(prev => [...prev, errorToast]);
    }
  };

  const handleToastClose = (toastId: string) => (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setToasts(prev => prev.filter(toast => toast.id !== toastId));
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          ポケソルテキスト
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            multiline
            fullWidth
            value={pokesolText}
            InputProps={{
              readOnly: true,
            }}
            variant="outlined"
            rows={8}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCopy}
            variant="contained"
            startIcon={<ContentCopyIcon />}
          >
            コピー
          </Button>
          <Button onClick={onClose}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Toast Messages - 複数のトーストを重ねて表示 */}
      {toasts.map((toast, index) => (
        <Snackbar
          key={toast.id}
          open={true}
          autoHideDuration={2000}
          onClose={handleToastClose(toast.id)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{
            // 複数のトーストを縦に重ねて表示
            transform: `translateY(-${index * 60}px)`,
          }}
        >
          <Alert 
            onClose={handleToastClose(toast.id)} 
            severity={toast.severity} 
            sx={{ width: '100%' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      ))}
    </>
  );
};

export default PokesolTextModal;
